import React, { useState, ElementRef } from 'react';
import { Button, Input, Divider } from 'antd';
import { useTranslation } from 'react-i18next';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons';

import { useAuth } from 'hooks';
import { getNetworkLogo, getSubdomain } from 'lib/helper';
import { auth, network as networkApi } from 'lib/api';
import { FormItem } from 'components/common/Forms/Forms';

import defaultLogo from 'assets/images/work+.svg';
import { useUserSecurity, useUsername } from '../LoginStore';
import * as S from './UsernameStepStyles';

type UsernameStepProps = {
  onNext: () => void;
};

export default function UsernameStep({ onNext }: UsernameStepProps) {
  const { t } = useTranslation('login');
  const { networkId, network } = useAuth();
  // console.log('Network info:', { network: network.samlProvider });

  const { username, setUsername } = useUsername();
  const { setUserSecurity } = useUserSecurity();

  const [logoUrl, setLogoUrl] = useState(() =>
    network.logo ? getNetworkLogo(network) : defaultLogo
  );
  const [loading, setLoading] = useState(false);
  const [samlLoading, setSamlLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [credentialsExpanded, setCredentialsExpanded] = useState(false);

  const usernameHandler = (e: React.ChangeEvent<ElementRef<'input'>>) => {
    const value = e.target.value;

    setUsername(value);
  };

  const continueHandler = async () => {
    setError(undefined);
    setLoading(true);

    let subdomain = getSubdomain(window.location.host);
    if (process.env.NODE_ENV !== 'production') subdomain = 'web-front-praga';

    const checkSubdomainResult = await networkApi.checkValidSubdomain({ username, subdomain });

    if (checkSubdomainResult.data) {
      const result = await auth.findDomainUser({ username, networkId });

      if (result.ok) {
        setUserSecurity(result.data);
      }
    }
    setLoading(false);
    setUsername(username);

    onNext();
  };

  const samlLoginHandler = async () => {
    try {
      setSamlLoading(true);

      await auth.loginSamlRedirect({
        networkId: network.networkId,
        platform: 'WebBrowser',
      });
    } catch (error) {
      setSamlLoading(false);
      console.error('Erro ao iniciar autenticação SAML', error);
    }
  };

  const keyUpHandler = (e: React.KeyboardEvent<ElementRef<'input'>>) => {
    if (e.key === 'Enter' && username.length > 0) {
      continueHandler();
    }
  };

  const credentialsForm = (
    <>
      <h1>{t('usernameStep.title')}</h1>
      <p>{t('usernameStep.description')}</p>

      <FormItem
        inputName="username"
        forInput="username"
        label={t('usernameStep.userEmail')}
        errors={
          error
            ? {
                username: {
                  message:
                    t(`usernameStep.errors.${error}`) +
                    ' ' +
                    t(`usernameStep.errors.${error}_description`),
                },
              }
            : undefined
        }
      >
        <Input
          id="username"
          css={S.input(!!error)}
          size="large"
          placeholder={t('usernameStep.userEmail')}
          autoFocus
          value={username}
          onChange={usernameHandler}
          onKeyUp={keyUpHandler}
        />
      </FormItem>

      <Button
        block
        type="primary"
        size="large"
        disabled={!username}
        loading={loading}
        onClick={continueHandler}
      >
        {t('usernameStep.continue')}
      </Button>
    </>
  );

  if (!network?.isSamlEnabled) {
    return (
      <S.Wrapper>
        <S.Logo src={logoUrl} alt="Network logo" onError={() => setLogoUrl(defaultLogo)} />
        {credentialsForm}
      </S.Wrapper>
    );
  }

  return (
    <S.Wrapper>
      <S.Logo src={logoUrl} alt="Network logo" onError={() => setLogoUrl(defaultLogo)} />

      <h1>{t('usernameStep.samlTitle')}</h1>
      <p>{t('usernameStep.samlDescription')}</p>

      <Button
        block
        type="primary"
        size="large"
        loading={samlLoading}
        onClick={() => samlLoginHandler()}
      >
        {t('usernameStep.samlButton') + (network?.samlProvider || 'SSO')}
      </Button>

      <Divider>{t('default:conjunction.or')}</Divider>

      <Button
        block
        type="link"
        aria-expanded={credentialsExpanded}
        onClick={() => setCredentialsExpanded(expanded => !expanded)}
      >
        {t('usernameStep.credentialsToggle')}
        <S.ToggleIcon icon={faChevronDown} expanded={credentialsExpanded} />
      </Button>

      {credentialsExpanded && <S.CredentialsSection>{credentialsForm}</S.CredentialsSection>}
    </S.Wrapper>
  );
}
